import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createBlock as _createBlock, vModelText as _vModelText, normalizeStyle as _normalizeStyle, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "p-3.5 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200/80 dark:border-blue-900/40 flex items-start gap-3 text-xs text-blue-800 dark:text-blue-300" }
const _hoisted_2 = { class: "flex flex-col items-center justify-center space-y-2" }
const _hoisted_3 = { class: "w-12 h-12 rounded-2xl bg-secondary/10 dark:bg-secondary-dark/20 flex items-center justify-center text-secondary dark:text-secondary-dark-text" }
const _hoisted_4 = {
  key: 0,
  class: "p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 flex items-center justify-between"
}
const _hoisted_5 = {
  key: 1,
  class: "p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3 text-xs"
}
const _hoisted_6 = { class: "flex items-center gap-2" }
const _hoisted_7 = { class: "font-semibold text-gray-700 dark:text-gray-300" }
const _hoisted_8 = { class: "flex items-center gap-2" }
const _hoisted_9 = ["value"]
const _hoisted_10 = {
  key: 2,
  class: "space-y-3"
}
const _hoisted_11 = { class: "flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400" }
const _hoisted_12 = { class: "space-y-3 max-h-96 overflow-y-auto pr-1" }
const _hoisted_13 = { class: "flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-700/60" }
const _hoisted_14 = { class: "flex items-center gap-2.5 min-w-0" }
const _hoisted_15 = { class: "p-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0" }
const _hoisted_16 = { class: "min-w-0" }
const _hoisted_17 = ["title"]
const _hoisted_18 = { class: "text-[11px] text-gray-500 dark:text-gray-400" }
const _hoisted_19 = { class: "flex items-center gap-2" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" }
const _hoisted_22 = ["onUpdate:modelValue"]
const _hoisted_23 = ["value"]
const _hoisted_24 = ["onUpdate:modelValue"]
const _hoisted_25 = ["onUpdate:modelValue"]
const _hoisted_26 = { class: "sm:col-span-2 md:col-span-3" }
const _hoisted_27 = ["onUpdate:modelValue"]
const _hoisted_28 = {
  key: 3,
  class: "space-y-1.5"
}
const _hoisted_29 = { class: "flex justify-between text-xs font-medium text-gray-700 dark:text-gray-300" }
const _hoisted_30 = { class: "w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden" }
const _hoisted_31 = { class: "flex items-center justify-between w-full" }
const _hoisted_32 = { class: "text-xs font-medium text-gray-500 dark:text-gray-400" }
const _hoisted_33 = { key: 0 }
const _hoisted_34 = { class: "font-bold text-gray-900 dark:text-white" }
const _hoisted_35 = { class: "flex items-center gap-3" }

import { ref, watch } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { employeeDocumentService } from '@/services/employeeDocumentService'
import { useToastService } from '@/services/toast'
import type { employee } from '@/types/employee'
import { 
  DOCUMENT_CATEGORIES, 
  type DocumentCategory,
  type EmployeeDocument
} from '@/types/employeeDocument'
import {
  UploadCloud,
  FileText,
  ImageIcon,
  Paperclip,
  Trash2,
  X,
  Info,
  Layers,
} from 'lucide-vue-next'

interface FileUploadItem {
  id: string
  file: File
  name: string
  category: DocumentCategory | ''
  expiry_date: string
  notes: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UploadStaffDocuments',
  props: {
    isVisible: { type: Boolean },
    employee: {},
    initialCategory: {}
  },
  emits: ["close", "success"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const toast = useToastService()

const fileItems = ref<FileUploadItem[]>([])
const batchCategory = ref<DocumentCategory | ''>(props.initialCategory || '')
const isDragging = ref(false)
const isUploading = ref(false)
const uploadProgress = ref(0)
const fileError = ref('')
const fileInputRef = ref<HTMLInputElement | null>(null)

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      batchCategory.value = props.initialCategory || ''
      fileItems.value = []
      fileError.value = ''
      uploadProgress.value = 0
    }
  }
)

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

const handleFileInput = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files) {
    addFiles(Array.from(target.files))
  }
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const handleDrop = (e: DragEvent) => {
  isDragging.value = false
  if (e.dataTransfer?.files) {
    addFiles(Array.from(e.dataTransfer.files))
  }
}

const formatCleanTitle = (fileName: string) => {
  const cleanBase = fileName.replace(/\.[^/.]+$/, '')
  return cleanBase
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase())
}

const addFiles = (files: File[]) => {
  fileError.value = ''
  const maxBytes = 32 * 1024 * 1024 // 32MB
  const allowedExtensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']

  for (const file of files) {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    if (!allowedExtensions.includes(ext)) {
      fileError.value = `The uploaded file(s) has an unsupported format. Allowed: PDF, DOC, DOCX, PNG, JPG.`
      return
    }
    if (file.size > maxBytes) {
      fileError.value = `File "${file.name}" exceeds the 32MB size limit.`
      return
    }
    // Prevent duplicate files
    if (!fileItems.value.some(item => item.file.name === file.name && item.file.size === file.size)) {
      fileItems.value.push({
        id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        file,
        name: formatCleanTitle(file.name),
        category: batchCategory.value || props.initialCategory || '',
        expiry_date: '',
        notes: '',
      })
    }
  }
}

const applyBatchCategory = () => {
  if (!batchCategory.value) {
    fileError.value = 'Please select a category before applying it to all documents.'
    return
  }

  fileItems.value.forEach(item => {
    item.category = batchCategory.value
  })
  toast.info(`Updated all files to "${getCategoryLabel(batchCategory.value)}"`)
}

const removeFile = (index: number) => {
  fileItems.value.splice(index, 1)
}

const clearAllFiles = () => {
  fileItems.value = []
}

const getCategoryLabel = (category: string) => {
  const item = DOCUMENT_CATEGORIES.find(c => c.value === category)
  return item ? item.label : category
}

const getCategoryBadgeClass = (category: string) => {
  const item = DOCUMENT_CATEGORIES.find(c => c.value === category)
  return item ? item.badgeClass : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const isPdf = (file: File) => file.type === 'application/pdf' || file.name.endsWith('.pdf')
const isDoc = (file: File) => file.name.endsWith('.doc') || file.name.endsWith('.docx')
const isImage = (file: File) => file.type.startsWith('image/') || /\.(jpg|jpeg|png)$/i.test(file.name)

const handleUpload = async () => {
  if (!props.employee || !props.employee.user_id) {
    toast.error('Cannot upload document: Missing employee user ID.')
    return
  }

  if (fileItems.value.length === 0) {
    fileError.value = 'Please select at least one document to upload.'
    return
  }

  const missingCategory = fileItems.value.some(item => !item.category)
  if (missingCategory) {
    fileError.value = 'Please select a category for every document before uploading.'
    return
  }

  const missingTitle = fileItems.value.some(item => !item.name.trim())
  if (missingTitle) {
    fileError.value = 'Please enter a document title for every document before uploading.'
    return
  }

  isUploading.value = true
  uploadProgress.value = 0

  try {
    const formData = new FormData()

    fileItems.value.forEach((item) => {
      formData.append('documents[]', item.file)
      formData.append('document_categories[]', item.category)
      formData.append('document_names[]', item.name.trim())
      formData.append('expiry_dates[]', item.expiry_date || '')
      formData.append('notes[]', item.notes.trim() || '')
    })

    const createdDocs = await employeeDocumentService.uploadDocuments(
      props.employee.user_id,
      formData,
      (event) => {
        if (event.total) {
          uploadProgress.value = Math.round((event.loaded * 100) / event.total)
        }
      }
    )

    toast.success(`${createdDocs.length} document(s) uploaded successfully.`)
    emit('success', createdDocs)
    emit('close')
  } catch (err: any) {
    const msg = err?.response?.data?.message || 'Failed to upload documents. Please try again.'
    toast.error(msg)
  } finally {
    isUploading.value = false
  }
}

const closeModal = () => {
  if (!isUploading.value) {
    emit('close')
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    "is-visible": _ctx.isVisible,
    title: "Upload Staff Documents",
    subtitle: _ctx.employee ? `Upload files & credentials for ${_ctx.employee.first_name} ${_ctx.employee.last_name}` : 'Upload employee documents',
    icon: _unref(UploadCloud),
    size: "3xl",
    type: "default",
    "show-footer": true,
    onClose: closeModal
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_31, [
        _createElementVNode("div", _hoisted_32, [
          (fileItems.value.length > 0)
            ? (_openBlock(), _createElementBlock("span", _hoisted_33, [
                _cache[13] || (_cache[13] = _createTextVNode(" Ready to upload: ", -1)),
                _createElementVNode("span", _hoisted_34, _toDisplayString(fileItems.value.length), 1),
                _cache[14] || (_cache[14] = _createTextVNode(" file(s) ", -1))
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_35, [
          _createVNode(BaseButton, {
            text: "Cancel",
            variant: "outline",
            size: "md",
            disabled: isUploading.value,
            onClick: closeModal
          }, null, 8, ["disabled"]),
          _createVNode(BaseButton, {
            text: fileItems.value.length > 1 ? `Upload ${fileItems.value.length} Files` : 'Upload Document',
            variant: "primary",
            size: "md",
            loading: isUploading.value,
            disabled: fileItems.value.length === 0,
            icon: _unref(UploadCloud),
            onClick: handleUpload
          }, null, 8, ["text", "loading", "disabled", "icon"])
        ])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _withModifiers(handleUpload, ["prevent"]),
        id: "uploadStaffDocsForm",
        class: "space-y-5"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_unref(Info), { class: "w-4 h-4 flex-shrink-0 text-blue-500 mt-0.5" }),
          _cache[4] || (_cache[4] = _createElementVNode("div", null, [
            _createElementVNode("span", { class: "font-semibold" }, "Multiple Files & Categorization:"),
            _createTextVNode(" You can upload multiple files at once and assign different categories (e.g. License, CPR Certificate, Paper Application Record) and expiration dates to each individual file below. ")
          ], -1))
        ]),
        _createElementVNode("div", null, [
          _createElementVNode("div", {
            class: _normalizeClass(["border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-200 cursor-pointer", [
            isDragging.value
              ? 'border-secondary bg-secondary/5 dark:bg-secondary-dark/10 scale-[1.01]'
              : 'border-gray-300 dark:border-gray-700 hover:border-secondary/60 bg-gray-50/50 dark:bg-gray-800/40'
          ]]),
            onDragover: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (isDragging.value = true), ["prevent"])),
            onDragleave: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (isDragging.value = false), ["prevent"])),
            onDrop: _withModifiers(handleDrop, ["prevent"]),
            onClick: triggerFileInput
          }, [
            _createElementVNode("input", {
              ref_key: "fileInputRef",
              ref: fileInputRef,
              type: "file",
              multiple: "",
              class: "hidden",
              accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
              onChange: handleFileInput
            }, null, 544),
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_unref(UploadCloud), { class: "w-6 h-6" })
              ]),
              _cache[5] || (_cache[5] = _createElementVNode("div", null, [
                _createElementVNode("p", { class: "text-sm font-semibold text-gray-800 dark:text-gray-200" }, [
                  _createElementVNode("span", { class: "text-secondary dark:text-secondary-dark-text underline underline-offset-2" }, "Click to browse"),
                  _createTextVNode(" or drag & drop files here ")
                ]),
                _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400 mt-1" }, " Accepted: PDF, DOC, DOCX, PNG, JPG (Max 32MB per file) • Select one or multiple files ")
              ], -1))
            ])
          ], 34)
        ]),
        (fileError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("span", null, _toDisplayString(fileError.value), 1),
              _createElementVNode("button", {
                type: "button",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (fileError.value = '')),
                class: "text-red-500 hover:text-red-700"
              }, [
                _createVNode(_unref(X), { class: "w-3.5 h-3.5" })
              ])
            ]))
          : _createCommentVNode("", true),
        (fileItems.value.length > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_unref(Layers), { class: "w-4 h-4 text-secondary dark:text-secondary-dark-text" }),
                _createElementVNode("span", _hoisted_7, "Set category for all " + _toDisplayString(fileItems.value.length) + " files:", 1)
              ]),
              _createElementVNode("div", _hoisted_8, [
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((batchCategory).value = $event)),
                  class: "px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-secondary"
                }, [
                  _cache[6] || (_cache[6] = _createElementVNode("option", {
                    value: "",
                    disabled: ""
                  }, "Select category", -1)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(DOCUMENT_CATEGORIES), (cat) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: cat.value,
                      value: cat.value
                    }, _toDisplayString(cat.label), 9, _hoisted_9))
                  }), 128))
                ], 512), [
                  [_vModelSelect, batchCategory.value]
                ]),
                _createElementVNode("button", {
                  type: "button",
                  onClick: applyBatchCategory,
                  class: "px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-secondary text-white hover:bg-secondary-dark transition"
                }, " Apply to All ")
              ])
            ]))
          : _createCommentVNode("", true),
        (fileItems.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("span", null, "Configured Documents (" + _toDisplayString(fileItems.value.length) + ")", 1),
                _createElementVNode("button", {
                  type: "button",
                  onClick: clearAllFiles,
                  class: "text-red-500 hover:text-red-700 normal-case font-medium text-xs transition"
                }, " Remove All ")
              ]),
              _createElementVNode("div", _hoisted_12, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fileItems.value, (item, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: item.id,
                    class: "p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xs space-y-3"
                  }, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("div", _hoisted_15, [
                          (isPdf(item.file))
                            ? (_openBlock(), _createBlock(_unref(FileText), {
                                key: 0,
                                class: "w-4 h-4 text-red-500"
                              }))
                            : (isDoc(item.file))
                              ? (_openBlock(), _createBlock(_unref(FileText), {
                                  key: 1,
                                  class: "w-4 h-4 text-blue-500"
                                }))
                              : (isImage(item.file))
                                ? (_openBlock(), _createBlock(_unref(ImageIcon), {
                                    key: 2,
                                    class: "w-4 h-4 text-emerald-500"
                                  }))
                                : (_openBlock(), _createBlock(_unref(Paperclip), {
                                    key: 3,
                                    class: "w-4 h-4 text-gray-500"
                                  }))
                        ]),
                        _createElementVNode("div", _hoisted_16, [
                          _createElementVNode("p", {
                            class: "text-xs font-bold text-gray-900 dark:text-white truncate",
                            title: item.file.name
                          }, _toDisplayString(item.file.name), 9, _hoisted_17),
                          _createElementVNode("p", _hoisted_18, _toDisplayString(formatFileSize(item.file.size)), 1)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_19, [
                        (item.category)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 0,
                              class: _normalizeClass([
                    'px-2 py-0.5 rounded-lg text-[10px] font-bold border hidden sm:inline-block',
                    getCategoryBadgeClass(item.category)
                  ])
                            }, _toDisplayString(getCategoryLabel(item.category)), 3))
                          : _createCommentVNode("", true),
                        _createElementVNode("button", {
                          type: "button",
                          onClick: ($event: any) => (removeFile(index)),
                          class: "p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition",
                          title: "Remove this file"
                        }, [
                          _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                        ], 8, _hoisted_20)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_21, [
                      _createElementVNode("div", null, [
                        _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1" }, [
                          _createTextVNode(" Category "),
                          _createElementVNode("span", { class: "text-red-500" }, "*")
                        ], -1)),
                        _withDirectives(_createElementVNode("select", {
                          "onUpdate:modelValue": ($event: any) => ((item.category) = $event),
                          class: "w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary",
                          required: ""
                        }, [
                          _cache[7] || (_cache[7] = _createElementVNode("option", {
                            value: "",
                            disabled: ""
                          }, "Select category", -1)),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(DOCUMENT_CATEGORIES), (cat) => {
                            return (_openBlock(), _createElementBlock("option", {
                              key: cat.value,
                              value: cat.value
                            }, _toDisplayString(cat.label), 9, _hoisted_23))
                          }), 128))
                        ], 8, _hoisted_22), [
                          [_vModelSelect, item.category]
                        ])
                      ]),
                      _createElementVNode("div", null, [
                        _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1" }, [
                          _createTextVNode(" Document Title "),
                          _createElementVNode("span", { class: "text-red-500" }, "*")
                        ], -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((item.name) = $event),
                          type: "text",
                          placeholder: "e.g. Driver's License",
                          class: "w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary",
                          required: ""
                        }, null, 8, _hoisted_24), [
                          [_vModelText, item.name]
                        ])
                      ]),
                      _createElementVNode("div", null, [
                        _cache[10] || (_cache[10] = _createElementVNode("label", { class: "block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1" }, " Expiration Date ", -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((item.expiry_date) = $event),
                          type: "date",
                          class: "w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
                        }, null, 8, _hoisted_25), [
                          [_vModelText, item.expiry_date]
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_26, [
                        _cache[11] || (_cache[11] = _createElementVNode("label", { class: "block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1" }, [
                          _createTextVNode(" Notes / Description "),
                          _createElementVNode("span", { class: "text-gray-400 font-normal" }, "(optional)")
                        ], -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((item.notes) = $event),
                          type: "text",
                          placeholder: "e.g. Scanned paper copy signed on file",
                          class: "w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
                        }, null, 8, _hoisted_27), [
                          [_vModelText, item.notes]
                        ])
                      ])
                    ])
                  ]))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true),
        (isUploading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
              _createElementVNode("div", _hoisted_29, [
                _cache[12] || (_cache[12] = _createElementVNode("span", null, "Uploading documents...", -1)),
                _createElementVNode("span", null, _toDisplayString(uploadProgress.value) + "%", 1)
              ]),
              _createElementVNode("div", _hoisted_30, [
                _createElementVNode("div", {
                  class: "bg-secondary h-2 rounded-full transition-all duration-300",
                  style: _normalizeStyle({ width: `${uploadProgress.value}%` })
                }, null, 4)
              ])
            ]))
          : _createCommentVNode("", true)
      ], 32)
    ]),
    _: 1
  }, 8, ["is-visible", "subtitle", "icon"]))
}
}

})